/**
 * The live bell badge (`specs/M5-notifications.md` §4.1).
 *
 * The api joins every socket to `user:<id>` on connect, and `notify()` emits one
 * `notification.created` per recipient carrying `userId` — so there is nothing to subscribe
 * to here beyond the envelope stream. `notification.read` / `.cleared` come from the user's
 * *other* tabs, which is what keeps two open windows in step.
 *
 * The socket is lazy (it opens on the first listener), so this hook only runs for a signed-in
 * shell — never on the login screen.
 */
import { useEffect, useState } from 'react';
import type { EventEnvelope } from '@elizmonday/shared';
import { onEvent } from '../../lib/realtime/socket';

export interface NotificationsRealtime {
  /** Bumped on every notification event; pass it to the panel to trigger a refetch. */
  revision: number;
  /** `null` until the first event; the shell's bootstrap value wins before then. */
  unreadDelta: number;
}

export function isNotificationEvent(event: Pick<EventEnvelope, 'type'>): boolean {
  return (
    event.type === 'notification.created' ||
    event.type === 'notification.read' ||
    event.type === 'notification.cleared'
  );
}

/**
 * The **Update feed's** own read-state signal (P-283). It is a different store from the bell, so
 * it bumps `revision` — which is what re-reads both unread counts — without touching
 * `unreadDelta`, the bell's optimistic counter.
 */
export function isInboxEvent(event: Pick<EventEnvelope, 'type'>): boolean {
  return event.type === 'inbox.changed';
}

export function useNotificationsRealtime(enabled: boolean): NotificationsRealtime {
  const [revision, setRevision] = useState(0);
  const [unreadDelta, setUnreadDelta] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    let stop: (() => void) | undefined;
    try {
      stop = onEvent((event) => {
        if (isInboxEvent(event)) {
          setRevision((current) => current + 1);
          return;
        }
        if (!isNotificationEvent(event)) return;
        setRevision((current) => current + 1);
        if (event.type === 'notification.created') setUnreadDelta((current) => current + 1);
        else setUnreadDelta(0);
      });
    } catch {
      // No socket (a blocked websocket, a test renderer): the badge stays at its bootstrap
      // value and the next fetch corrects it. Never a thrown error in the shell.
    }
    return () => stop?.();
  }, [enabled]);

  return { revision, unreadDelta };
}
